/**
 * Login Page
 *
 * Provides email/password login and OAuth sign-in options.
 * Handles MFA verification if required.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, AlertCircle, Loader2, Eye, EyeOff, Shield, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import type { OAuthProviderType } from '../config/firebase';
import OAuthButtons from '../components/auth/OAuthButtons';
import MFAVerification from '../components/auth/MFAVerification';
import { ContentSageIcon } from '../components/ContentSageLogo';
import analyticsService from '../services/analytics';

const TOTP_CODE_LENGTH = 6;
const TOTP_MAX_INDEX = TOTP_CODE_LENGTH - 1;

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signInWithOAuth, mfaState, error, loading, clearError, checkMFARequired, verifyTOTPCode } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');

  // Add state for MFA
  const [showMFA, setShowMFA] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Get the redirect path from location state, default to dashboard
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  // Cleanup credentials on unmount for security
  useEffect(() => {
    return () => {
      setLoginEmail('');
      setLoginPassword('');
    };
  }, []);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    clearError();

    if (!email || !password) {
      setLocalError('Please enter both email and password.');
      return;
    }

    try {
      // Check if MFA is required
      const mfaRequired = await checkMFARequired(email, password);

      if (mfaRequired) {
        // Store credentials and show MFA input
        setLoginEmail(email);
        setLoginPassword(password);
        setShowMFA(true);
        return;
      }

      // No MFA required - proceed with normal sign in
      await signIn(email, password);
      analyticsService.trackLoginComplete('email', '');
      navigate(from, { replace: true });
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Login failed');
      analyticsService.trackAuthError('login', error ?? 'Unknown error');
    }
  };

  const handleOAuthLogin = async (provider: OAuthProviderType) => {
    setLocalError('');
    clearError();

    try {
      await signInWithOAuth(provider);
      navigate(from, { replace: true });
    } catch {
      // Error is already set in context
    }
  };

  // Add MFA verification handler
  const handleMFAVerify = async (code: string) => {
    try {
      setLocalError('');
      clearError();
      await verifyTOTPCode(loginEmail, loginPassword, code);
      analyticsService.trackLoginComplete('email', '');
      navigate(from, { replace: true });
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Verification failed');
    }
  };

  const displayError = localError || error;

  // Show MFA verification if required (Phone SMS MFA)
  if (mfaState.required) {
    return <MFAVerification onSuccess={() => navigate(from, { replace: true })} />;
  }

  // TOTP MFA Verification Component (inline)
  const TOTPVerification = () => {
    const [totpCode, setTotpCode] = useState(['', '', '', '', '', '']);
    const [verifying, setVerifying] = useState(false);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
      inputRefs.current[0]?.focus();
    }, []);

    const handleChange = (index: number, value: string) => {
      if (value && !/^\d$/.test(value)) return;

      const newCode = [...totpCode];
      newCode[index] = value;
      setTotpCode(newCode);

      if (value && index <= TOTP_MAX_INDEX) {
        inputRefs.current[index + 1]?.focus();
      }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !totpCode[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    };

    const handleVerify = useCallback(async () => {
      const fullCode = totpCode.join('');
      if (fullCode.length !== TOTP_CODE_LENGTH) return;

      setVerifying(true);
      try {
        await handleMFAVerify(fullCode);
      } catch (err) {
        setTotpCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      } finally {
        setVerifying(false);
      }
    }, [totpCode]);

    useEffect(() => {
      const fullCode = totpCode.join('');
      if (fullCode.length === TOTP_CODE_LENGTH && totpCode.every((digit) => digit !== '')) {
        handleVerify();
      }
    }, [totpCode, handleVerify]);

    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="sm:mx-auto sm:w-full sm:max-w-md"
        >
          <button
            onClick={() => {
              setShowMFA(false);
              setLocalError('');
              clearError();
            }}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8 transition"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to sign in
          </button>

          <div className="bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-100">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-purple-600" />
            </div>

            <h2 className="text-center text-2xl font-bold text-gray-900 mb-2">
              Two-Factor Authentication
            </h2>
            <p className="text-center text-gray-600 mb-8">
              Enter the 6-digit code from your authenticator app
            </p>

            {displayError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3"
              >
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{displayError}</p>
              </motion.div>
            )}

            <div className="flex justify-center gap-2 mb-6">
              {totpCode.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  disabled={verifying}
                  className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition disabled:opacity-50"
                />
              ))}
            </div>

            <button
              onClick={handleVerify}
              disabled={verifying || totpCode.some((d) => !d)}
              className="w-full bg-purple-600 text-white py-3 rounded-xl font-semibold hover:bg-purple-700 focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {verifying ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify Code'
              )}
            </button>
          </div>
        </motion.div>
      </div>
    );
  };

  // Show TOTP verification if MFA is required
  if (showMFA) {
    return <TOTPVerification />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 overflow-auto pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="sm:mx-auto sm:w-full sm:max-w-md"
      >
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <ContentSageIcon size={40} />
          <span className="text-2xl font-bold bg-gradient-to-r from-blue-500 via-purple-500 to-amber-500 bg-clip-text text-transparent">
            ContentSage
          </span>
        </Link>

        <h2 className="text-center text-3xl font-bold text-gray-900 mb-2">
          Welcome back
        </h2>
        <p className="text-center text-gray-600 mb-8">
          Sign in to continue to your dashboard
        </p>

        {/* Login Form */}
        <div className="bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-100">
          {/* Error Message */}
          {displayError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3"
            >
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{displayError}</p>
            </motion.div>
          )}

          {/* OAuth Buttons */}
          <OAuthButtons onOAuthClick={handleOAuthLogin} disabled={loading} />

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">or continue with email</span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleEmailLogin} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition"
                  placeholder="you@example.com"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-1">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-purple-600 hover:text-purple-700"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="password"
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition"
                  placeholder="Enter your password"
                  disabled={loading}
                  autoComplete="current-password"
                  style={showPassword ? undefined : { WebkitTextSecurity: 'disc' } as React.CSSProperties}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-purple-600 text-white py-3 rounded-xl font-semibold hover:bg-purple-700 focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>
        </div>

        {/* Sign Up Link */}
        <p className="mt-8 text-center text-gray-600">
          Don't have an account?{' '}
          <Link to="/signup" className="text-purple-600 font-semibold hover:text-purple-700">
            Sign up for free
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
